import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { Box, Button, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'

import {
  DrinkCounterContext,
  DrinkCounterContextInterface,
} from '../../../providers/DrinkCounter/DrinkCounterProvider'
import { MUMRecordObj } from '../../../providers/MUM/MUMProvider'
import { BACObj } from '../../../providers/PatientChartPage/BACProvider'
import {
  PatientChartPageContext,
  PatientChartPageContextInterface,
} from '../../../providers/PatientChartPage/PatientChartPage.provider'
import {
  SubstanceCounterContext,
  SubstanceCounterContextInterface,
  TrackedSubstanceObj,
} from '../../../providers/SubstanceCounter/SubstanceCounter.Provider'
import { themeColors } from '../../../theme'
import { dateFormatters } from '../../../utilities/HelperFunctions'
import { useStyles } from '../PatientChartPage.styles'
import { PatientResourceCheck } from '../PatientResourceCheck'
import { CreateTrackedSubstanceModal } from './CreateTrackedSubstanceModal'
import { DiscontinueTrackedSubstanceModal } from './DiscontinueTrackedSubstanceModal'
import { LamictalScheduleReview } from './LamictalScheduleReview'
import { PassportTable } from './PassportTable'
import {
  QuestionnaireGroupSummary,
  QuestionnaireList,
} from './QuestionnaireList'
import { QuestionnaireManagement } from './QuestionnaireManagement'
import { ReviewBacMumTable } from './ReviewBacMumTable'
import { ReviewBacOrDrinkTypesModal } from './ReviewBacOrDrinkTypesModal'

interface MyJourneyReviewTableInterface {
  routedPatientId: string | undefined
  selectedReviewOption: string
  ReviewBACTableTabs: string[]
  ReviewMUMTableTabs: string[]
  videoReviewFilter: boolean
  setVideoReviewFilter: React.Dispatch<React.SetStateAction<boolean>>
  filteredBacWithVideos: BACObj[]
  setFilteredBacWithVideos: React.Dispatch<React.SetStateAction<BACObj[]>>
  filteredMUMVideos: MUMRecordObj[]
  setFilteredMUMVideos: React.Dispatch<React.SetStateAction<MUMRecordObj[]>>
}

export const MyJourneyReviewTable = ({
  routedPatientId,
  selectedReviewOption,
  ReviewBACTableTabs,
  ReviewMUMTableTabs,
  videoReviewFilter,
  setVideoReviewFilter,
  filteredBacWithVideos,
  setFilteredBacWithVideos,
  filteredMUMVideos,
  setFilteredMUMVideos,
}: MyJourneyReviewTableInterface) => {
  const { classes } = useStyles()

  const { patientDrinkTypes, getPatientDrinkTypes } = useContext(
    DrinkCounterContext
  ) as DrinkCounterContextInterface

  const { activeChartPatient, registerForDrugScreens } = useContext(
    PatientChartPageContext
  ) as PatientChartPageContextInterface

  const { patientTrackedSubstances, getPatientTrackedSubstancesForProvider } =
    useContext(SubstanceCounterContext) as SubstanceCounterContextInterface

  const [showReviewModal, setShowReviewModal] = useState(false)

  const [showCreateTrackedSubstanceModal, setShowCreateTrackedSubstanceModal] =
    useState(false)
  const [showDiscontinueModal, setShowDiscontinueModal] = useState(false)
  const [trackedSubstanceDiscontinueId, setTrackedSubstanceDiscontinueId] =
    useState(0)
  const [selectedQuestionnaireSummary, setSelectedQuestionnaireSummary] =
    useState<QuestionnaireGroupSummary>({
      questionnaireId: 0,
      title: '',
      activeAssignment: false,
      lastSubmissionDate: '',
      totalSubmissions: 0,
    })

  const handleDrugScreenRegistration = async () => {
    await registerForDrugScreens(
      activeChartPatient.patientId,
      !activeChartPatient.drugScreenChartData?.needsRandomDrugScreens
    )
  }

  useEffect(() => {
    let isMounted = true

    const fetchData = async () => {
      if (isMounted) {
        await getPatientDrinkTypes(Number(routedPatientId))
        await getPatientTrackedSubstancesForProvider(Number(routedPatientId))
      }
    }
    fetchData()

    return () => {
      isMounted = false
    }
  }, [routedPatientId])

  const calculateDrugScreenScheduledDate = () => {
    if (activeChartPatient.drugScreenChartData?.weeklyDrugScreenDate) {
      const date = dayjs(
        activeChartPatient.drugScreenChartData?.weeklyDrugScreenDate
      ).format('MM/DD/YYYY')

      if (date === '01/01/1901') {
        return 'Date will be assigned Sunday Evening'
      }

      return date
    }
    return ''
  }

  if (
    selectedReviewOption === 'Review MUM' &&
    filteredMUMVideos.some((v) => v.videoURL)
  ) {
    return (
      <ReviewBacMumTable
        routedPatientId={routedPatientId}
        selectedReviewOption={selectedReviewOption}
        reviewTableTabs={ReviewMUMTableTabs}
        filteredMUMVideos={filteredMUMVideos}
        videoReviewFilter={videoReviewFilter}
        setVideoReviewFilter={setVideoReviewFilter}
        setFilteredMUMVideos={setFilteredMUMVideos}
        showReviewModal={showReviewModal}
        setShowReviewModal={setShowReviewModal}
      />
    )
  }
  if (
    selectedReviewOption === 'Review BAC' &&
    filteredBacWithVideos?.some((v) => v.videoURL)
  ) {
    return (
      <ReviewBacMumTable
        routedPatientId={routedPatientId}
        selectedReviewOption={selectedReviewOption}
        reviewTableTabs={ReviewBACTableTabs}
        filteredBacWithVideos={filteredBacWithVideos}
        videoReviewFilter={videoReviewFilter}
        setVideoReviewFilter={setVideoReviewFilter}
        setFilteredBacWithVideos={setFilteredBacWithVideos}
        showReviewModal={showReviewModal}
        setShowReviewModal={setShowReviewModal}
      />
    )
  }
  if (
    selectedReviewOption === 'Review Drink Types' &&
    patientDrinkTypes?.some((r) => r.name)
  ) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <table className={classes.reviewTable}>
          <thead className={classes.tableThead}>
            <tr>
              <th className={classes.tableLabelTh} scope='col'>
                Drink types
              </th>
              <th className={classes.tableLabelTh} scope='col'>
                Alcohol Content (%)
              </th>
              <th className={classes.tableLabelTh} scope='col'>
                Drink Ounces
              </th>
            </tr>
          </thead>

          <tbody>
            {patientDrinkTypes?.map((t, i) => {
              return (
                <tr key={`table-drinkType-${i}`}>
                  <th className={classes.tableTh} scope='col'>
                    {t.name}
                  </th>
                  <th className={classes.tableTh} scope='col'>
                    {t.alcoholContent}
                  </th>
                  <th className={classes.tableTh} scope='col'>
                    {t.drinkOunces}
                  </th>
                </tr>
              )
            })}

            <ReviewBacOrDrinkTypesModal
              routedPatientId={routedPatientId}
              selectedReviewOption={selectedReviewOption}
              showReviewModal={showReviewModal}
              setShowReviewModal={setShowReviewModal}
            />
          </tbody>
        </table>

        <Button
          onClick={() => {
            setShowReviewModal(true)
          }}
          variant='contained'
          style={{
            fontSize: '16px',
            marginTop: '10px',
            width: '170px',
            backgroundColor: themeColors.red,
            borderColor: 'transparent',
            borderRadius: '5px',
            alignSelf: 'flex-end',
          }}
        >
          Reset Drink Types
        </Button>
      </div>
    )
  }
  if (selectedReviewOption === 'Resources') {
    return <PatientResourceCheck routedPatientId={routedPatientId} />
  }
  if (selectedReviewOption === 'Review Substance Counter') {
    const currentPatientTrackedSubstances = patientTrackedSubstances.filter(
      (p: TrackedSubstanceObj) => p.isActive
    )

    const historyPatientTrackedSubstances = patientTrackedSubstances.filter(
      (p: TrackedSubstanceObj) => !p.isActive
    )

    const renderMedicationTable = (
      groupedPatientTrackedSubstances: TrackedSubstanceObj[]
    ) => {
      return (
        <table className={classes.reviewTable}>
          <thead className={classes.tableThead}>
            <tr>
              <th className={classes.tableLabelTh} scope='col'>
                Substance
              </th>
              <th className={classes.tableLabelTh} scope='col'>
                Drug Form
              </th>
              <th className={classes.tableLabelTh} scope='col'>
                Dosage Unit
              </th>
              <th className={classes.tableLabelTh} scope='col'>
                Start Date
              </th>
              <th className={classes.tableLabelTh} scope='col'>
                Discontinue Date
              </th>
              <th className={classes.tableLabelTh} scope='col'></th>
            </tr>
          </thead>

          <tbody>
            {groupedPatientTrackedSubstances.map(
              (t: TrackedSubstanceObj, i) => {
                return (
                  <tr key={`table-drinkType-${i}`}>
                    <th className={classes.tableTh} scope='col'>
                      {t.substance?.name}
                    </th>

                    <th className={classes.tableTh} scope='col'>
                      {t.drugForm?.name}
                    </th>

                    <th className={classes.tableTh} scope='col'>
                      {t.dosageUnit?.unitName}
                    </th>

                    <th className={classes.tableTh} scope='col'>
                      {dayjs(t.createdAt).format('MM/DD/YYYY')}
                    </th>

                    <th className={classes.tableTh} scope='col'>
                      <Button
                        variant={t.isActive ? 'contained' : 'text'}
                        onClick={() => {
                          if (t.id) {
                            setTrackedSubstanceDiscontinueId(t.id)
                            setShowDiscontinueModal(true)
                          }
                        }}
                        disabled={!t.isActive}
                        sx={{
                          alignItems: 'center',
                          fontSize: '14px',
                        }}
                      >
                        {t.isActive ? <DeleteOutlineIcon /> : null}

                        {t.isActive ? (
                          'Discontinue'
                        ) : t.modifiedAt ? (
                          <Typography color={themeColors.gray40} fontSize={13}>
                            {dateFormatters.prettyDate(t.modifiedAt)}
                          </Typography>
                        ) : null}
                      </Button>
                    </th>
                  </tr>
                )
              }
            )}
          </tbody>
        </table>
      )
    }

    return (
      <Box>
        <Button
          onClick={() => {
            setShowCreateTrackedSubstanceModal(true)
          }}
          variant='contained'
          style={{
            fontSize: '16px',
            margin: '20px 0',
            width: '270px',
          }}
        >
          Create New Tracked Substance
        </Button>

        <Typography fontWeight={700} style={{ margin: '15px 0' }}>
          Current
        </Typography>
        {currentPatientTrackedSubstances.length ? (
          renderMedicationTable(currentPatientTrackedSubstances)
        ) : (
          <Typography fontWeight={700} style={{ margin: '15px 0' }}>
            No prescription found.
          </Typography>
        )}

        <Typography fontWeight={700} style={{ margin: '25px 0 15px 0' }}>
          History
        </Typography>
        {historyPatientTrackedSubstances.length ? (
          renderMedicationTable(historyPatientTrackedSubstances)
        ) : (
          <Typography fontWeight={700} style={{ margin: '15px 0' }}>
            No prescription found.
          </Typography>
        )}

        <CreateTrackedSubstanceModal
          routedPatientId={routedPatientId}
          showCreateTrackedSubstanceModal={showCreateTrackedSubstanceModal}
          setShowCreateTrackedSubstanceModal={
            setShowCreateTrackedSubstanceModal
          }
        />

        <DiscontinueTrackedSubstanceModal
          routedPatientId={routedPatientId}
          showDiscontinueModal={showDiscontinueModal}
          setShowDiscontinueModal={setShowDiscontinueModal}
          trackedSubstanceDiscontinueId={trackedSubstanceDiscontinueId}
        />
      </Box>
    )
  }
  if (selectedReviewOption === 'Review Drug Screens') {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <p style={{ margin: '15px 33px' }}>
            Patient is{' '}
            {activeChartPatient.drugScreenChartData?.needsRandomDrugScreens ? (
              ''
            ) : (
              <strong>NOT </strong>
            )}
            registered for Random Drug Screens
          </p>

          <Button
            variant='contained'
            style={{
              fontSize: '16px',

              width: '280px',

              margin: '15px 33px',
            }}
            onClick={() => handleDrugScreenRegistration()}
          >
            {activeChartPatient.drugScreenChartData?.needsRandomDrugScreens
              ? 'End Random Drug Screens?'
              : 'Register for Random Drug Screens'}
          </Button>
        </div>

        {activeChartPatient.drugScreenChartData?.needsRandomDrugScreens && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <p style={{ margin: '15px 33px' }}>
              Next Scheduled Drug Screen:{' '}
              <strong>{calculateDrugScreenScheduledDate()}</strong>
            </p>

            {!activeChartPatient.drugScreenChartData.completed && (
              <p style={{ margin: '15px 33px' }}>
                Patient Scheduled For:{' '}
                <strong>
                  {activeChartPatient.drugScreenChartData.patientScheduled
                    ? dayjs(
                        activeChartPatient.drugScreenChartData?.patientScheduled
                      ).format('MM/DD/YYYY hh:mma')
                    : 'Not scheduled yet'}
                </strong>
              </p>
            )}

            <p style={{ margin: '15px 33px' }}>
              Completed:{' '}
              <strong>
                {activeChartPatient.drugScreenChartData.completed
                  ? 'Yes'
                  : 'No'}
              </strong>
            </p>
          </div>
        )}
      </>
    )
  }
  if (selectedReviewOption === 'Review Passport') {
    return <PassportTable patientId={routedPatientId} />
  }
  if (selectedReviewOption === 'Questionnaires' && routedPatientId) {
    if (!selectedQuestionnaireSummary.questionnaireId) {
      return (
        <QuestionnaireList
          patientId={parseInt(routedPatientId)}
          setSelectedQuestionnaireSummary={setSelectedQuestionnaireSummary}
        />
      )
    } else if (
      selectedQuestionnaireSummary.title.includes('SSRI') &&
      routedPatientId
    ) {
      return (
        <QuestionnaireManagement
          patientId={parseInt(routedPatientId)}
          questionnaireGroupSummary={selectedQuestionnaireSummary}
          recurrenceWeeks={8}
          recurrenceIntervalDays={7}
          onBack={() =>
            setSelectedQuestionnaireSummary({
              questionnaireId: 0,
              title: '',
              activeAssignment: false,
              lastSubmissionDate: '',
              totalSubmissions: 0,
            })
          }
        />
      )
    } else {
      return <Typography>Coming Soon</Typography>
    }
  }
  if (selectedReviewOption === 'Lamictal Schedule') {
    return <LamictalScheduleReview patientId={routedPatientId} />
  } else {
    return (
      <span className={classes.tableCaption}>
        This patient has no record yet.
      </span>
    )
  }
}
